import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../api';
import './BattleScreen.css';
import * as BattleAnimations from './BattleAnimations';
import { toast } from 'react-toastify';
import {
  rubyGummyImg,
  sapphireGummyImg,
  emeraldGummyImg,
  goldenGummyImg,
  diamondGummyImg,
  pouchIcon,
} from "../../constants/items";
import BattleInventoryPanel from "./Components/BattleInventoryPanel";
import "./Components/PokemonTargetSelection.css";

// Utility function to calculate level from experience
const getLevel = (totalexp) => {
  if (!totalexp) return 1;
  const lvl = Math.floor(Math.pow(totalexp, 1/3));
  // Bump up if we've passed the next threshold
  return Math.pow(lvl + 1, 3) <= totalexp ? lvl + 1 : lvl;
};


export default function BattleScreen({
  initialData,    // { battle_id, player: { …, stats }, foe: { …, stats } }
  onExit,
  battleBgUrl = "/Battle/green.png",
  onBattleEnd,
  pvpUpdate       // PvP real-time updates
}) {
  const playerRef   = useRef(null);
  const opponentRef = useRef(null);
  const logRef = useRef(null);

  const [displayedExp, setDisplayedExpState] = useState(0);
  const displayedExpRef = useRef(0);
  const expInitializedRef = useRef(false);
  const expAnimFrameRef = useRef(null);
  const displayedLevelRef = useRef(null);
  const [levelUpEvent, setLevelUpEvent] = useState(null);
  const expTotalRef = useRef(null);
  const setDisplayedExp = useCallback((val) => {
    const v = Number(val) || 0;
    displayedExpRef.current = v;
    setDisplayedExpState(v);
  }, []);

  const animateExpTo = useCallback((target) => {
    const targetVal = Number(target || 0);
    if (!Number.isFinite(targetVal)) return;

    if (!expInitializedRef.current) {
      expInitializedRef.current = true;
      setDisplayedExp(targetVal);
      displayedLevelRef.current = getLevel(targetVal);
      return;
    }

    const from = Number(displayedExpRef.current || 0);
    if (targetVal === from) return;

    if (expAnimFrameRef.current) {
      cancelAnimationFrame(expAnimFrameRef.current);
      expAnimFrameRef.current = null;
    }

    if (targetVal < from) {
      setDisplayedExp(targetVal);
      displayedLevelRef.current = getLevel(targetVal);
      return;
    }

    const diff = targetVal - from;
    const durationMs = Math.min(2500, Math.max(650, diff * 4));
    const start = performance.now();

    const step = (now) => {
      const pct = Math.min(1, (now - start) / durationMs);
      const next = Math.round(from + diff * pct);
      setDisplayedExp(next);

      const nextLvl = getLevel(next);
      const prevLvl = displayedLevelRef.current;
      if (typeof prevLvl === "number" && nextLvl > prevLvl) {
        displayedLevelRef.current = nextLvl;
        setLevelUpEvent({ level: nextLvl, key: `${Date.now()}-${nextLvl}` });
        setTimeout(() => setLevelUpEvent(null), 1200);
      } else if (typeof prevLvl !== "number") {
        displayedLevelRef.current = nextLvl;
      }

      if (pct < 1) {
        expAnimFrameRef.current = requestAnimationFrame(step);
      } else {
        expAnimFrameRef.current = null;
      }
    };

    expAnimFrameRef.current = requestAnimationFrame(step);
  }, [setDisplayedExp]);

  const [roster, setRoster]         = useState([]);
  const [yourMon, setYourMon]       = useState(null);
  const [opponent, setOpponent]     = useState(null);
  const [battleId, setBattleId]     = useState(null);
  const [animationConfig, setAnimationConfig] = useState(null);
  const [animKey, setAnimKey]       = useState(0);
  const [logs, setLogs]             = useState([]);
  const [battleOver, setBattleOver] = useState(false);
  const [forceEntranceAnimation, setForceEntranceAnimation] = useState(0);
  const recentLogsRef = useRef(new Set()); // Track recent logs to prevent duplicates
  const { opponent_type } = initialData;
  const lastEntranceSwitchIdRef = useRef(null);
  
  // PvP-specific state
  const [currentTurn, setCurrentTurn] = useState(null);
  const [isMyTurn, setIsMyTurn] = useState(true); // Default true for NPC battles
  const [waitingForOpponent, setWaitingForOpponent] = useState(false); // Default true for NPC battles
  const [mustRecharge, setMustRecharge] = useState(false); // Track if Pokemon must recharge
  const rechargeExecutedRef = useRef(false); // Track if recharge has been executed
  
  // Get current username for PvP turn checking
  // For PvP, we should use the username from the battle data to ensure consistency
  const getCurrentUsername = () => {
    // For PvP battles, use the player's name from initialData
    if (opponent_type === "pvp" && initialData.player) {
      // The backend sends the player data with the correct username
      // We need to get it from the challenge/battle record
      const token = localStorage.getItem("jwtToken");
      if (token) {
        try {
          const decoded = JSON.parse(atob(token.split('.')[1]));
          return decoded.sub || decoded.username;
        } catch (e) {
          console.error("Error decoding JWT:", e);
        }
      }
      return localStorage.getItem("authUser");
    }
    
    // For non-PvP battles, use JWT/localStorage
    const token = localStorage.getItem("jwtToken");
    if (token) {
      try {
        const decoded = JSON.parse(atob(token.split('.')[1]));
        return decoded.sub || decoded.username;
      } catch (e) {
        return localStorage.getItem("authUser");
      }
    }
    return localStorage.getItem("authUser");
  };
  
  // Helper to get opponent data (PvP uses 'opponent', NPC uses 'foe')
  const getFoeData = (res) => res.opponent || res.foe;
  
  // Helper to check if player won (handles both NPC and PvP)
  const didPlayerWin = (res) => {
    if (opponent_type === "pvp") {
      const myUsername = getCurrentUsername();
      return res.winner === myUsername;
    }
    return res.winner === 'player';
  };
  
  // Helper to check if player lost (handles both NPC and PvP)
  const didPlayerLose = (res) => {
    if (opponent_type === "pvp") {
      const myUsername = getCurrentUsername();
      return res.winner && res.winner !== myUsername;
    }
    return res.winner === 'foe';
  };

  
  // Create sanitizeLog as a memoized function to avoid dependency issues
  const sanitizeLog = useCallback((msg) => {
    const foeName = initialData.opponent_type === "trainer"
      ? initialData.trainer_name
      : initialData.foe.name || "The opponent";
    return msg.replace(/\bfoe\b/gi, foeName);
  }, [initialData]);
  
  const addLog = useCallback(msg => {
    // Convert message to string for comparison (handles React elements)
    const msgString = typeof msg === 'string' ? msg : JSON.stringify(msg);
    
    // Check if this exact message was recently added (within last 2 seconds)
    const now = Date.now();
    const logKey = `${msgString}-${Math.floor(now / 2000)}`; // Group by 2-second windows
    
    if (recentLogsRef.current.has(logKey)) {
      return; // Skip duplicate
    }
    
    // Add to recent logs and clean old entries
    recentLogsRef.current.add(logKey);
    
    // Clean old entries (older than 10 seconds)
    const cutoff = Math.floor(now / 2000) - 5;
    recentLogsRef.current.forEach(key => {
      const timestamp = parseInt(key.split('-').pop());
      if (timestamp < cutoff) {
        recentLogsRef.current.delete(key);
      }
    });
    
    setLogs(l => [...l, msg]);
    
    // Check if message indicates recharge is needed
    if (msgString.includes("must recharge next turn")) {
      setMustRecharge(true);
      rechargeExecutedRef.current = false; // Reset flag when recharge is needed
    }
    // Check if message indicates recharging (clears the flag)
    if (msgString.includes("must recharge!")) {
      setMustRecharge(false);
      rechargeExecutedRef.current = false; // Reset flag after recharge completes
    }
  }, [setLogs]);
  
  // Auto-execute recharge turn when Pokemon must recharge
  useEffect(() => {
    if (mustRecharge && yourMon && yourMon.currentHp > 0 && !battleOver && !animationConfig && !rechargeExecutedRef.current) {
      // For PvP, only auto-execute if it's our turn
      if (opponent_type === "pvp" && !isMyTurn) {
        return;
      }
      
      rechargeExecutedRef.current = true; // Mark as executed to prevent duplicates
      
      // Use any move (it will be blocked by backend and trigger recharge)
      const anyMove = yourMon.moves && yourMon.moves.length > 0 ? yourMon.moves[0] : null;
      if (anyMove) {
        // Small delay to ensure UI updates
        const timer = setTimeout(async () => {
          // Directly call doAction to bypass UI checks
          await doAction({ action: 'move', move: anyMove.name });
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mustRecharge, yourMon, battleOver, animationConfig, opponent_type, isMyTurn]);

  useEffect(() => {
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);

      // Check if this is a battle API call
      if (args[0] && typeof args[0] === 'string' && args[0].includes('/v1/battle/') && !args[0].includes('/start')) {
        const clonedResponse = response.clone();
        try {
          const data = await clonedResponse.json()

          // Store the last battle response for reference
          window.lastBattleResponse = data;

          // Enhanced defeat detection logic - ONLY trust explicit battle_over and winner flags
          const isDefeat = data.battle_over === true && data.winner === 'foe';

          if (isDefeat && !battleOver) {
            setBattleOver(true);
            addLog(`You were defeated by ${data.foe?.name || 'the opponent'}...`);

            // Check if this is a Battle Tower battle
            if (initialData.opponent_type === "battle_tower" && initialData.battle_tower_floor) {
              const floor = initialData.battle_tower_floor;

              // Show defeat message
              addLog(`💀 You were defeated on floor ${floor}!`);
              addLog(`You will be reset to a checkpoint floor.`);

              // Redirect to Battle Tower with loss parameter after a delay
              setTimeout(() => {
                window.location.href = `/battle-tower?battle_result=loss&floor=${floor}`;
              }, 3000);
            } else {
              // For non-Battle Tower battles, just call onBattleEnd
              setTimeout(() => {
                //console.log('🚨 INTERCEPTED DEFEAT: Calling onBattleEnd');
                onBattleEnd?.({
                  winner: 'foe',
                  opponent_type: initialData.opponent_type,
                  floor: initialData.battle_tower_floor
                });
              }, 2000);
            }
          }
        } catch (e) {
          // Ignore JSON parsing errors
          console.error('Error parsing battle response:', e);
        }
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [battleOver, onBattleEnd, initialData, addLog]);

  const [gummyStatus, setGummyStatus] = useState({
    ruby_gummy: 0,
    sapphire_gummy: 0,
    emerald_gummy: 0,
    golden_gummy: 0,
    diamond_gummy: 0,
  });

  useEffect(() => {
    api
      .get("/v1/gummy-status", { withCredentials: true })
      .then((r) => {
        setGummyStatus(r.data);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (logRef.current) {
      requestAnimationFrame(() => {
        if (logRef.current) {
          logRef.current.scrollTop = logRef.current.scrollHeight;
        }
      });
    }
  }, [logs]);

  useEffect(() => {
    setLogs([]);
    recentLogsRef.current.clear();
    if (logRef.current) {
      logRef.current.scrollTop = 0;
    }
  }, [initialData?.battle_id]);

  useEffect(() => {
    if (!yourMon) return;
    const target = Number(yourMon.totalexp || 0);
    expTotalRef.current = target;
    animateExpTo(target);

    return () => {
      if (expAnimFrameRef.current) {
        cancelAnimationFrame(expAnimFrameRef.current);
        expAnimFrameRef.current = null;
      }
    };
  }, [yourMon, yourMon?.totalexp, animateExpTo]);

  const onCompleteFinal = useCallback(() => {
    setAnimationConfig(null);
  }, [setAnimationConfig]);

  // ── Bag & inventory state ──
  const [showBag, setShowBag] = useState(false);
  const toggleBag = async () => {
    // Don't allow opening bag if active Pokémon is fainted
    if (!showBag && yourMon && yourMon.currentHp <= 0) {
      return;
    }
    
    // For PvP battles, check item restrictions before opening bag
    if (!showBag && opponent_type === "pvp") {
      try {
        const response = await api.get(`/v1/pvp/battle/${battleId}/info`, { withCredentials: true });
        const battleInfo = response.data;
        
        if (!battleInfo.items_allowed) {
          toast.error("Items are not allowed in this battle!", {
            position: "top-center",
            autoClose: 3000
          });
          return;
        }
        
        const myUsername = getCurrentUsername();
        const isChallenger = myUsername === battleInfo.challenger_username;
        const myItemsUsed = isChallenger ? battleInfo.challenger_items_used : battleInfo.opponent_items_used;
        
        if (battleInfo.max_items_per_player > 0 && myItemsUsed >= battleInfo.max_items_per_player) {
          toast.error(`You have reached the maximum item limit (${battleInfo.max_items_per_player} items)`, {
            position: "top-center",
            autoClose: 3000
          });
          return;
        }
      } catch (error) {
        console.error("Failed to check item restrictions:", error);
      }
    }
    
    setShowBag(v => !v);
  };
  // ── Bag state ──
  const [inventory, setInventory] = useState([]);   // array of { id, amount }
  const [selectedAction, setSelectedAction] = useState(null);

// Fetch trainer items when bag is opened the first time
useEffect(() => {
  if (!showBag) return;
  // Always refetch when bag is opened to get the latest item counts
  api
    .get('/v1/trainer-items', { withCredentials: true })
    .then(r => {
      // Use the API response directly
      setInventory(r.data);
    })
    .catch(console.error);
}, [showBag]);

const battleCoinsSrc = `/items/battle_coin.png`;

// Foe entrance animation – play each time the trainer sends out a new Pokémon
const lastFoeKeyRef = useRef(null);
  useEffect(() => {
    //console.log('=== ENTRANCE ANIMATION USEEFFECT ===');
    if (!(initialData.opponent_type === 'trainer' || initialData.opponent_type === 'battle_tower')) return;
    if (!opponent) return;

    const isNewSwitch = opponent.switchId && opponent.switchId !== lastEntranceSwitchIdRef.current;
    const isFullHp = opponent.currentHp === opponent.maxHp;
    const isNewSpecies = lastFoeKeyRef.current !== opponent.species;
    const isNewSpeciesAtFullHp = isFullHp && isNewSpecies;

    // Only trigger on new species at full HP (natural Pokemon switch)
    const shouldTrigger = isNewSpeciesAtFullHp;
    //console.log('isNewSwitch:', isNewSwitch, 'isNewSpeciesAtFullHp:', isNewSpeciesAtFullHp, 'isNewSpecies:', isNewSpecies, 'isFullHp:', isFullHp, '=>', shouldTrigger);
    
    if (!shouldTrigger) return;

    //console.log('🎬 TRIGGERING ENTRANCE ANIMATION');
    setAnimationConfig({
      key: 'FoeEntranceAnimation',
      props: { opponentRef, onComplete: onCompleteFinal }
    });
    setAnimKey(k => k + 1);

    lastFoeKeyRef.current = opponent.species;
    if (opponent.switchId) lastEntranceSwitchIdRef.current = opponent.switchId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opponent]);



  const logBattleCoins = useCallback((amount) =>
  addLog(
    <span>
      You earned{" "}
      <img
        src={battleCoinsSrc}
        alt="Battle coin"
        style={{ width: 24, verticalAlign: "middle" }}
      />{" "}
      <strong>{amount.toLocaleString()}</strong> Battle Coins!
    </span>
  ), [addLog, battleCoinsSrc]);


  useEffect(() => {
    // Skip if battle is already over or if we don't have player data yet
    if (battleOver || !yourMon || !roster.length) return;

    // Check if player's active Pokémon is fainted and all party Pokémon are fainted
    const isPlayerFainted = yourMon.currentHp <= 0;
    const allPartyFainted = roster.every(p => p.current_hp <= 0);

    // Only declare defeat if both conditions are true AND we've received a battle_over=true from backend
    // This prevents premature defeat messages when you can still switch Pokemon
    if (isPlayerFainted && allPartyFainted && window.lastBattleResponse?.battle_over === true) {
      setBattleOver(true);

      const foeName = initialData.opponent_type === "trainer"
        ? initialData.trainer_name
        : (opponent?.species || "The opponent");

      addLog(`You were defeated by ${foeName}...`);

      // Check if this is an aggressive battle - apply penalty
      if (initialData?.engagement === "aggro") {
        api
          .post(
            "/v1/battle/aggressive-penalty",
            { map_id: initialData.map_id || 103 },
            { withCredentials: true }
          )
          .then((penaltyRes) => {
            const lostCoins = penaltyRes.data?.lost_coins || 0;
            const lostMoney = penaltyRes.data?.lost_money || 0;
            
            if (lostCoins > 0) {
              addLog(`💸 Lost ${lostCoins} coins to the aggressive Pokémon!`);
            } else if (lostMoney > 0) {
              addLog(`💸 Lost $${lostMoney.toLocaleString()} to the aggressive Pokémon!`);
            }
          })
          .catch((err) => {
            console.error("Aggressive penalty error:", err);
          });
      }

      // Check if this is a Battle Tower battle
      if (initialData.opponent_type === "battle_tower" && initialData.battle_tower_floor) {
        const floor = initialData.battle_tower_floor;

        // Show defeat message
        addLog(`💀 You were defeated on floor ${floor}!`);
        addLog(`You will be reset to a checkpoint floor.`);

        // Redirect to Battle Tower with loss parameter after a delay
        setTimeout(() => {
          window.location.href = `/battle-tower?battle_result=loss&floor=${floor}`;
        }, 3000);
      } else {
        // For non-Battle Tower battles, call onBattleEnd
        setTimeout(() => {
          onBattleEnd?.({
            winner: 'foe',
            opponent_type: initialData.opponent_type,
            floor: initialData.battle_tower_floor
          });
        }, 2000);
      }
    }
  }, [battleOver, yourMon, roster, initialData, opponent, onBattleEnd, addLog]);

  // Track if the battle ended by running away
  const [ranAway, setRanAway] = useState(false);

  useEffect(() => {
    // Only auto-close if battle is over AND it's a wild battle that ended by running away
    if (!battleOver || !(ranAway && opponent_type === "wild")) return;
    
    const timer = setTimeout(() => {
      onExit();
    }, 3000);
    return () => clearTimeout(timer);
  }, [battleOver, onExit, ranAway, opponent_type]);
  
  // Effect to handle battle errors from the backend
  useEffect(() => {
    const handleApiErrors = () => {
      const originalFetch = window.fetch;
      window.fetch = async (...args) => {
        const response = await originalFetch(...args);
        if (response.url.includes('/v1/battle/') && !response.ok) {
          const data = await response.clone().json().catch(() => ({}));
          if (response.status === 400 && data.detail === "You can only interact with your most recent battle") {
            toast.warning("This battle was ended because you started a new battle", {
              position: "top-center",
              autoClose: 5000
            });
            setTimeout(() => onExit(), 3000);
          }
        }
        return response;
      };
      return () => {
        window.fetch = originalFetch;
      };
    };
    return handleApiErrors();
  }, [onExit]);

  // Initialize from initialData
  useEffect(() => {
    let mounted = true;
    // For PvP battles, use battle-specific roster; for others, use regular roster
    const rosterEndpoint = opponent_type === "pvp" 
      ? `/v1/pvp/battle/${initialData.battle_id}/roster`
      : '/v1/roster';
    
    api.get(rosterEndpoint, { withCredentials: true }).then(r => {
      if (!mounted) return;
      const rd = r.data;
      setRoster(rd);
      let active = rd.find(p => p.current_hp > 0) || rd.find(p => p.slot === 1);
      setYourMon({
        slot: initialData.player.slot,
        species: initialData.player.name,
        level: initialData.player.level,
        currentHp: initialData.player.current_hp,
        maxHp: initialData.player.stats.hp,
        stats: initialData.player.stats,
        moves: initialData.player.moves,
        totalexp: active?.totalexp || 0
      });

      setBattleId(initialData.battle_id);
      
      // For PvP, roster already has correct HP values from battle participant table
      if (opponent_type !== "pvp") {
        setRoster(rs => rs.map(p =>
          p.slot === initialData.player.slot
            ? { ...p, current_hp: initialData.player.current_hp }
            : p
        ));
      }
      setOpponent({
        species: initialData.foe.name,
        level: initialData.foe.level,
        currentHp: initialData.foe.current_hp,
        maxHp: initialData.foe.stats.hp,
        stats: initialData.foe.stats,
        moves: initialData.foe.moves
      });

      setBattleId(initialData.battle_id);
      setRoster(rs => rs.map(p =>
        p.slot === initialData.player.slot
          ? { ...p, current_hp: initialData.player.current_hp }
          : p
      ));
      
      // Initialize PvP turn state
      if (initialData.opponent_type === "pvp" && initialData.current_turn) {
        setCurrentTurn(initialData.current_turn);
        const myUsername = getCurrentUsername();
        const isMyTurnValue = initialData.current_turn === myUsername;
        setIsMyTurn(isMyTurnValue);
        // Reset waiting state if it's my turn
        if (isMyTurnValue) {
          setWaitingForOpponent(false);
        }
      }
    }).catch(console.error);
    return () => { mounted = false; };
  }, [initialData]);

  // Handle PvP real-time updates
  useEffect(() => {
    if (!pvpUpdate) return;
    
    // Add battle logs for ALL PvP updates (not just dual execution)
    if (pvpUpdate.log && pvpUpdate.log.length > 0) {
      pvpUpdate.log.forEach(line => addLog(line));
    }
    
    // Handle forced switch notifications
    if (pvpUpdate.action === "switch" && !pvpUpdate.both_moves_executed) {
      setWaitingForOpponent(false);
    }
    
    if (pvpUpdate.both_moves_executed) {
      setWaitingForOpponent(false);
      
      // Logs are now handled at the top level for all PvP updates
      
      // Play both animations in order (only for moves, not switches/items)
      const myUsername = getCurrentUsername();
      const iGoFirst = pvpUpdate.first_player === myUsername;
      
      // Check if we have any animations to play
      const hasFirstAnimation = pvpUpdate.first_move !== null && pvpUpdate.first_move !== undefined;
      const hasSecondAnimation = pvpUpdate.second_move !== null && pvpUpdate.second_move !== undefined;
      
      if (hasFirstAnimation && iGoFirst) {
        // I attack first
        triggerAnimation(pvpUpdate.first_move, playerRef, opponentRef, () => {
          // Then opponent attacks
          if (hasSecondAnimation && !pvpUpdate.battle_over) {
            setTimeout(() => {
              triggerAnimation(pvpUpdate.second_move, opponentRef, playerRef, onCompleteFinal);
            }, 500);
          } else {
            onCompleteFinal();
          }
        });
      } else if (hasFirstAnimation && !iGoFirst) {
        // Opponent attacks first
        triggerAnimation(pvpUpdate.first_move, opponentRef, playerRef, () => {
          // Then I attack
          if (hasSecondAnimation && !pvpUpdate.battle_over) {
            setTimeout(() => {
              triggerAnimation(pvpUpdate.second_move, playerRef, opponentRef, onCompleteFinal);
            }, 500);
          } else {
            onCompleteFinal();
          }
        });
      } else if (hasSecondAnimation) {
        // Only second action has animation (first was switch/item)
        const secondPlayerIsMe = pvpUpdate.second_player === myUsername;
        if (secondPlayerIsMe) {
          triggerAnimation(pvpUpdate.second_move, playerRef, opponentRef, onCompleteFinal);
        } else {
          triggerAnimation(pvpUpdate.second_move, opponentRef, playerRef, onCompleteFinal);
        }
      } else {
        // No animations (both were switches/items or second fainted)
        onCompleteFinal();
      }
    }
    
    // Update turn state
    if (pvpUpdate.current_turn) {
      setCurrentTurn(pvpUpdate.current_turn);
      const myUsername = getCurrentUsername();
      const isMyTurnValue = pvpUpdate.current_turn === myUsername;
      setIsMyTurn(isMyTurnValue);
      // Reset waiting state based on turn
      setWaitingForOpponent(!isMyTurnValue);
    }
    
    // Update player state
    if (pvpUpdate.player) {
      const newHp = pvpUpdate.player.current_hp;
      const activePartyMember = pvpUpdate.party?.find(p => p.species === pvpUpdate.player.name);
      const speciesChanged = yourMon?.species !== pvpUpdate.player.name;
      
      setYourMon(mon => ({
        ...mon,
        species: pvpUpdate.player.name,
        level: pvpUpdate.player.level,
        slot: pvpUpdate.player.slot || activePartyMember?.slot || mon.slot,
        currentHp: newHp,
        maxHp: pvpUpdate.player.stats.hp,
        stats: pvpUpdate.player.stats,
        moves: pvpUpdate.player.moves,
        switchId: speciesChanged ? Date.now() : mon.switchId // Update switchId if species changed
      }));
      
      // Check if player's Pokemon fainted and force switch
      if (newHp <= 0 && opponent_type === "pvp") {
        // Clear waiting state to allow switching
        setWaitingForOpponent(false);
        
        toast.error("Your Pokemon fainted! You MUST switch to another Pokemon!", {
          position: "top-center",
          autoClose: false,
          closeButton: false
        });
        
        // Check if there are any alive Pokemon to switch to
        setTimeout(() => {
          const alivePokemon = roster.filter(p => p.current_hp > 0 && p.slot !== yourMon?.slot);
          if (alivePokemon.length === 0) {
            toast.error("All your Pokemon have fainted! You lost the battle.", {
              position: "top-center"
            });
          }
        }, 100);
      }
    }
    
    // Update opponent state (PvP uses 'opponent', NPC uses 'foe')
    const opponentData = pvpUpdate.opponent || pvpUpdate.foe;
    if (opponentData) {
      setOpponent(opp => {
        // Only update switchId if the species actually changed (real switch)
        const speciesChanged = !opp || opp.species !== opponentData.name;
        const newOpponent = {
          ...opp,
          species: opponentData.name,
          level: opponentData.level,
          currentHp: opponentData.current_hp,
          maxHp: opponentData.stats.hp,
          stats: opponentData.stats,
          moves: opponentData.moves,
          switchId: speciesChanged ? Date.now() : opp?.switchId // Only update on species change
        };
        return newOpponent;
      });
    }
    
    // Update party
    if (pvpUpdate.party) {
      setRoster(rs => rs.map(p => {
        const upd = pvpUpdate.party.find(m => m.slot === p.slot);
        if (upd) {
          return { ...p, current_hp: upd.current_hp, max_hp: upd.max_hp };
        }
        return p;
      }));
    }
    
    // Handle battle end
    if (pvpUpdate.battle_over && !battleOver) {
      setBattleOver(true);
      
      const myUsername = getCurrentUsername();
      const didIWin = pvpUpdate.winner === myUsername;
      
      if (didIWin) {
        addLog(`🏆 You won the battle!`);
        if (pvpUpdate.coins_won > 0) {
          addLog(`💰 You won ${pvpUpdate.coins_won.toLocaleString()} ${pvpUpdate.currency_type}!`);
        }
      } else {
        addLog(`💀 You lost the battle to ${pvpUpdate.winner}...`);
        if (pvpUpdate.coins_lost > 0) {
          addLog(`💸 You lost ${pvpUpdate.coins_lost.toLocaleString()} ${pvpUpdate.currency_type}...`);
        }
      }
      
      // Show battle end notification
      setTimeout(() => {
        if (onBattleEnd) {
          onBattleEnd({
            winner: didIWin ? 'player' : 'foe',
            opponent_type: 'pvp',
            coins_won: pvpUpdate.coins_won || 0,
            coins_lost: pvpUpdate.coins_lost || 0,
            currency_type: pvpUpdate.currency_type
          });
        }
      }, 3000);
    }
  }, [pvpUpdate, opponent_type, yourMon?.slot]);

  // Animation runner
  const triggerAnimation = useCallback((moveName, attackerRef, defenderRef, onFinish) => {
    // Convert move name to PascalCase (e.g., "iron tail" -> "IronTail")
    const formattedMoveName = moveName
      .replace(/-/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join('');
    const keyName = `${formattedMoveName}Animation`;
    let Comp = BattleAnimations[keyName];
    let resolvedKey = keyName;
    if (!Comp) {
      const normalized = keyName.toLowerCase();
      const altKey = Object.keys(BattleAnimations).find(k => k.toLowerCase() === normalized);
      if (altKey) {
        resolvedKey = altKey;
        Comp = BattleAnimations[altKey];
      }
    }
    if (!Comp) { console.warn('Battle animation not found', { moveName, formattedMoveName, keyName }); onFinish(); return; }

    const isFoe = attackerRef === opponentRef;
    if (isFoe && attackerRef.current) attackerRef.current.classList.add('attack');
    setAnimationConfig({
        key: resolvedKey,
      props: {
        playerRef: attackerRef,
        opponentRef: defenderRef,
        onComplete: () => {
          if (isFoe && attackerRef.current) attackerRef.current.classList.remove('attack');
          onFinish();
        }
      }
    });
    setAnimKey(k => k + 1);
  }, [opponentRef, setAnimKey]);

  const triggerHealAnimation = useCallback((side = 'player', onFinish) => {
    const ref = side === 'foe' ? opponentRef : playerRef;
    setAnimationConfig({
      key: 'FloralHealingAnimation',
      props: {
        playerRef: ref,
        opponentRef: side === 'foe' ? playerRef : opponentRef,
        onComplete: onFinish
      }
    });
    setAnimKey(k => k + 1);
  }, [opponentRef, playerRef]);

  // Raw fetch (no state updates)
  const fetchAction = useCallback(payload => {
    // Use PvP endpoint for PvP battles, regular endpoint for NPC battles
    const endpoint = opponent_type === "pvp" 
      ? `/v1/pvp/battle/${battleId}/action`
      : `/v1/battle/${battleId}/action`;
    
    return api.post(endpoint, payload, { withCredentials: true })
       .then(r => r.data)
       .catch(err => {
         if (err.response && err.response.status === 400 && 
             err.response.data.detail === "You can only interact with your most recent battle") {
           toast.warning("This battle was ended because you started a new battle", {
             position: "top-center",
             autoClose: 5000
           });
           setTimeout(() => onExit(), 3000);
         } else if (err.response && err.response.status === 400 && 
                    err.response.data.detail === "It's not your turn") {
           toast.warning("It's not your turn yet! Wait for your opponent.", {
             position: "top-center",
             autoClose: 2000
           });
         } else {
           console.error(err);
         }
         return null;
       });
  }, [battleId, onExit, opponent_type]);

  const parseExpFromLog = useCallback((logLines) => {
    if (!Array.isArray(logLines)) return null;
    for (const line of logLines) {
      if (typeof line !== "string") continue;
      const match = line.match(/([0-9][0-9,]*)\s*EXP/i);
      if (match && match[1]) {
        const num = Number(String(match[1]).replace(/,/g, ""));
        if (Number.isFinite(num)) return num;
      }
    }
    return null;
  }, []);

  const applyExpUpdateFromResponse = useCallback((res, slotFallback) => {
    if (!res || opponent_type === "pvp") return;
    const party = Array.isArray(res.party) ? res.party : [];
    const activePartyMember = party.find(m => Number(m?.owned_id) === Number(res?.player?.id))
      || party.find(m => m.species === res?.player?.name)
      || party.find(m => m.slot === (slotFallback || 1));

    const expGained = Number.isFinite(Number(res?.exp_gained))
      ? Number(res.exp_gained)
      : parseExpFromLog(res?.log);

    const baseExpSnapshot = Number(expTotalRef.current ?? yourMon?.totalexp ?? 0);
    const partyExpSnapshot = Number.isFinite(Number(activePartyMember?.totalexp))
      ? Number(activePartyMember.totalexp)
      : null;
    const shouldSyncExp = Number.isFinite(expGained)
      && expGained > 0
      && (!Number.isFinite(partyExpSnapshot) || partyExpSnapshot <= baseExpSnapshot);

    let nextTotalExp = null;
    setYourMon(mon => {
      const baseExp = Number(expTotalRef.current ?? mon.totalexp ?? 0);
      const partyExp = Number.isFinite(Number(activePartyMember?.totalexp))
        ? Number(activePartyMember.totalexp)
        : null;
      let computedTotalExp = baseExp;
      if (Number.isFinite(partyExp) && partyExp > computedTotalExp) {
        computedTotalExp = partyExp;
      }
      if (Number.isFinite(expGained) && expGained > 0 && (!Number.isFinite(partyExp) || partyExp <= baseExp)) {
        computedTotalExp = baseExp + Number(expGained);
      }
      nextTotalExp = Number(computedTotalExp || 0);
      expTotalRef.current = nextTotalExp;
      return { ...mon, totalexp: computedTotalExp };
    });

    if (Number.isFinite(nextTotalExp) && nextTotalExp > 0) {
      animateExpTo(nextTotalExp);
    }

    if (shouldSyncExp) {
      api.get('/v1/roster', { withCredentials: true }).then(r => {
        const rosterData = r?.data || [];
        const active = rosterData.find(p => Number(p.slot) === Number(slotFallback || 1));
        if (active && Number(active.totalexp || 0) > Number(expTotalRef.current || 0)) {
          const syncedTotal = Number(active.totalexp || 0);
          expTotalRef.current = syncedTotal;
          setYourMon(mon => ({
            ...mon,
            totalexp: syncedTotal,
            level: active.level || mon.level
          }));
          animateExpTo(syncedTotal);
        }
      }).catch(() => null);
    }
  }, [animateExpTo, api, opponent_type, parseExpFromLog, setYourMon, yourMon?.totalexp]);

  // Full update (for player-first)
  const doAction = useCallback(payload =>
    fetchAction(payload).then(res => {
      if (!res) return null; // Handle error case

      if (res.events && Array.isArray(res.events)) {
        const healEvt = res.events.find(e => e && e.type === 'heal');
        if (healEvt && healEvt.side === 'player' && typeof healEvt.new_hp === 'number') {
          setYourMon(mon => ({
            ...mon,
            currentHp: healEvt.new_hp
          }));
        }
        if (healEvt && healEvt.side === 'foe' && typeof healEvt.new_hp === 'number') {
          setOpponent(opp => ({
            ...opp,
            currentHp: healEvt.new_hp
          }));
        }
      }
      
      const foeName =
    initialData.opponent_type === "trainer"
      ? initialData.trainer_name
      : initialData.foe.name || "The opponent";

      const sanitizeLog = (msg) => msg.replace(/\bfoe\b/gi, foeName);
      const activePartyMember = res.party.find(m => Number(m?.owned_id) === Number(res?.player?.id))
        || res.party.find(m => m.species === res.player.name)
        || res.party.find(m => m.slot === (yourMon?.slot || 1));

      const newSlot = activePartyMember ? activePartyMember.slot : yourMon?.slot;

      const expGained = Number.isFinite(Number(res?.exp_gained))
        ? Number(res.exp_gained)
        : parseExpFromLog(res?.log);

      const baseExpSnapshot = Number(expTotalRef.current ?? yourMon?.totalexp ?? 0);
      const partyExpSnapshot = Number.isFinite(Number(activePartyMember?.totalexp))
        ? Number(activePartyMember.totalexp)
        : null;
      const shouldSyncExp = opponent_type !== "pvp"
        && Number.isFinite(expGained)
        && expGained > 0
        && (!Number.isFinite(partyExpSnapshot) || partyExpSnapshot <= baseExpSnapshot);
      
      // Handle PvP waiting state
      if (opponent_type === "pvp" && res.waiting_for_opponent) {
        if (!waitingForOpponent) {
          setWaitingForOpponent(true);
          setIsMyTurn(false); // It's now the opponent's turn
          addLog("Waiting for opponent to choose their move...");
        }
        return res;
      }
      
      // Handle PvP dual execution
      if (opponent_type === "pvp" && res.both_moves_executed) {
        setWaitingForOpponent(false);
        
        // Don't add logs from API response - they come via WebSocket for both players
        // This prevents duplicate logs for the player who acted second
        
        // Play both animations in order (only for moves, not switches/items)
      const myUsername = getCurrentUsername();
      const iGoFirst = res.first_player === myUsername;
      
      // Check if we have any animations to play
      const hasFirstAnimation = res.first_move !== null && res.first_move !== undefined;
      const hasSecondAnimation = res.second_move !== null && res.second_move !== undefined;
      
      if (hasFirstAnimation && iGoFirst) {
        // I attack first
        triggerAnimation(res.first_move, playerRef, opponentRef, () => {
          // Then opponent attacks
          if (hasSecondAnimation && !res.battle_over) {
            setTimeout(() => {
              triggerAnimation(res.second_move, opponentRef, playerRef, onCompleteFinal);
            }, 500);
          } else {
            onCompleteFinal();
          }
        });
      } else if (hasFirstAnimation && !iGoFirst) {
        // Opponent attacks first
        triggerAnimation(res.first_move, opponentRef, playerRef, () => {
          // Then I attack
          if (hasSecondAnimation && !res.battle_over) {
            setTimeout(() => {
              triggerAnimation(res.second_move, playerRef, opponentRef, onCompleteFinal);
            }, 500);
          } else {
            onCompleteFinal();
          }
        });
      } else if (hasSecondAnimation) {
        // Only second action has animation (first was switch/item)
        const secondPlayerIsMe = res.second_player === myUsername;
        if (secondPlayerIsMe) {
          triggerAnimation(res.second_move, playerRef, opponentRef, onCompleteFinal);
        } else {
          triggerAnimation(res.second_move, opponentRef, playerRef, onCompleteFinal);
        }
      } else {
        // No animations (both were switches/items or second fainted)
        onCompleteFinal();
      }  
        
        // Don't return early - continue to update state below
      }
      
      // Update PvP turn state
      if (opponent_type === "pvp" && res.current_turn) {
        setCurrentTurn(res.current_turn);
        const myUsername = getCurrentUsername();
        const isMyTurnValue = res.current_turn === myUsername;
        setIsMyTurn(isMyTurnValue);
        // Reset waiting state based on turn
        setWaitingForOpponent(!isMyTurnValue);
      }
      
      let nextTotalExp = null;
      setYourMon(mon => {
        const baseExp = Number(expTotalRef.current ?? mon.totalexp ?? 0);
        const partyExp = Number.isFinite(Number(activePartyMember?.totalexp))
          ? Number(activePartyMember.totalexp)
          : null;
        let computedTotalExp = baseExp;
        if (Number.isFinite(partyExp) && partyExp > computedTotalExp) {
          computedTotalExp = partyExp;
        }
        if (Number.isFinite(expGained) && expGained > 0 && (!Number.isFinite(partyExp) || partyExp <= baseExp)) {
          computedTotalExp = baseExp + Number(expGained);
        }
        nextTotalExp = Number(computedTotalExp || 0);
        expTotalRef.current = nextTotalExp;

        return {
          ...mon,
          slot: newSlot,
          species: res.player.name,
          level: res.player.level,
          totalexp: computedTotalExp,
          currentHp: res.player.current_hp,
          maxHp: res.player.stats.hp,
          stats: res.player.stats,
          // Preserve moves during recharge - only update if moves are provided
          moves: res.player.moves && res.player.moves.length > 0 ? res.player.moves : (mon.moves || [])
        };
      });

      if (Number.isFinite(nextTotalExp) && nextTotalExp > 0 && opponent_type !== "pvp") {
        animateExpTo(nextTotalExp);
      }

      if (shouldSyncExp) {
        api.get('/v1/roster', { withCredentials: true }).then(r => {
          const rosterData = r?.data || [];
          const active = rosterData.find(p => Number(p.slot) === Number(newSlot))
            || rosterData.find(p => Number(p.slot) === Number(yourMon?.slot || 1));
          if (active && Number(active.totalexp || 0) > Number(expTotalRef.current || 0)) {
            const syncedTotal = Number(active.totalexp || 0);
            expTotalRef.current = syncedTotal;
            setYourMon(mon => ({
              ...mon,
              totalexp: syncedTotal,
              level: active.level || mon.level
            }));
            animateExpTo(syncedTotal);
          }
        }).catch(() => null);
      }
  
      setRoster(rs => rs.map(p => {
        const upd = res.party.find(m => m.slot === p.slot);
        return upd ? { ...p, current_hp: upd.current_hp } : p;
      }));
  
      // PvP uses 'opponent' field, NPC battles use 'foe' field
      const foeData = res.opponent || res.foe;
      
      setOpponent(opp => ({
        ...opp,
        species: foeData.name,
        level: foeData.level,
        currentHp: foeData.current_hp,
        maxHp: foeData.stats.hp,
        stats: foeData.stats,
        moves: foeData.moves
      }));

      if (res.events && Array.isArray(res.events)) {
        const healEvt = res.events.find(e => e && e.type === 'heal');
        if (healEvt && healEvt.side === 'player' && healEvt.reason === 'leftovers') {
          triggerHealAnimation('player', onCompleteFinal);
        }
        if (healEvt && healEvt.side === 'foe' && healEvt.reason === 'trainer_heal') {
          triggerHealAnimation('foe', onCompleteFinal);
        }
      }
      
      if (res.battle_over === true && res.winner === 'foe' && !battleOver) {
        setBattleOver(true);
        addLog(`You were defeated by ${foeName}...`);

        // Check if this is a Battle Tower battle
        if (initialData.opponent_type === "battle_tower" && initialData.battle_tower_floor) {
          const floor = initialData.battle_tower_floor;

          // Show defeat message
          addLog(`💀 You were defeated on floor ${floor}!`);
          addLog(`You will be reset to a checkpoint floor.`);

          // Redirect to Battle Tower with loss parameter after a delay
          setTimeout(() => {
            window.location.href = `/battle-tower?battle_result=loss&floor=${floor}`;
          }, 3000);
        } else {
          // For non-Battle Tower battles, just call onBattleEnd
          setTimeout(() => {
            onBattleEnd?.({
              winner: 'foe',
              opponent_type: initialData.opponent_type,
              floor: initialData.battle_tower_floor
            });
          }, 2000);
        }

        return res; // Exit early to prevent further processing
      }

      // Check if this is a "Trainer sent out X" followed by "Foe used Y" scenario
      const hasSentOutLog = res.log && res.log.some(entry => 
        entry.toLowerCase().includes('battle tower trainer sent out') ||
        entry.toLowerCase().includes('trainer sent out') ||
        entry.toLowerCase().includes('sent out'));
      const hasFoeAttackLog = res.log && res.log.some(entry => entry.toLowerCase().includes('foe used'));
      const hasSentOutAndAttack = hasSentOutLog && hasFoeAttackLog;
      
      // If we have both "sent out" and "foe used" in the logs, we need to handle both animations
      if (hasSentOutAndAttack && !res.battle_over) {
        // First add all logs except the attack log
        const attackLogIndex = res.log.findIndex(entry => entry.toLowerCase().includes('foe used'));
        const nonAttackLogs = [...res.log];
        const attackLog = nonAttackLogs.splice(attackLogIndex, 1)[0];
        
        // Add non-attack logs first
        nonAttackLogs.forEach(line => addLog(sanitizeLog(line)));
        
        // Trigger the entrance animation for the new Pokemon
  
        setAnimKey(k => k + 1);
      } else {
        // Normal case - just add all logs
        res.log.forEach(line => addLog(sanitizeLog(line)));
        
        // Check if this is a recharge scenario with opponent attack
        const hasRechargeLog = res.log && res.log.some(entry => entry.includes("must recharge!"));
        const hasFoeAttackLog = res.log && res.log.some(entry => entry.toLowerCase().includes('foe used'));
        
        if (hasRechargeLog && hasFoeAttackLog && !res.battle_over) {
          // Extract the move name from the foe attack log
          const foeAttackEntry = res.log.find(entry => entry.toLowerCase().includes('foe used'));
          if (foeAttackEntry) {
            const moveMatch = foeAttackEntry.match(/foe used (.+?)!/i);
            if (moveMatch) {
              const moveName = moveMatch[1].trim();
              
              // Trigger opponent attack animation but don't return early
              setTimeout(() => {
                triggerAnimation(moveName, opponentRef, playerRef, onCompleteFinal);
              }, 500); // Small delay to ensure logs are processed
            }
          }
        }
      }
  
      // EXP earned log
     
if (!res.battle_over && res.exp_gained > 0) {
  addLog(`You gained ${res.exp_gained.toLocaleString()} EXP!`);
}
  
// Item drop handling will be moved to after battle_over processing

// Handle battle over state
if (res.battle_over) {
  setBattleOver(true);

  if (didPlayerWin(res)) {
    // Set opponent HP to 0
    setOpponent(opp => ({ ...opp, currentHp: 0 }));
    
    addLog(`🏆 You defeated ${sanitizeLog('foe')}!`);

    // Add EXP message
    if (res.exp_gained > 0) {
      addLog(`You gained ${res.exp_gained.toLocaleString()} EXP from defeating ${sanitizeLog('foe')}!`);
    }
   
    // Add item drop message if present
    if (res.item_drop) {
      let itemName = res.item_drop;
      let cleanName = res.item_drop;
      if(itemName === "Yellow Shard"){
        itemName = "yellowshard"
      }
      else if(itemName === "Metal Coat"){
        itemName ="metalcoat"
      }else if(itemName === "Lucky Punch"){
        itemName = "lucky-punch"
      }else if(itemName === "Dragon Fang"){
        itemName = "dragongfang"
      }else if(itemName === "Dragon%20Fang"){
        itemName = "dragongfang"
      }else if(itemName === "Lucky%20Punch"){
        itemName = "lucky-punch"
      }else if(itemName === "Metal%20Coat"){
        itemName ="metalcoat"
      }else if(itemName === "Lucky%20Punch"){
        itemName = "lucky-punch"
      }
      else if(itemName === "Ruby"){
        itemName = "ruby"
      }else if(itemName === "Sapphire"){
        itemName = "sapphire"
      }else if(itemName === "Emerald"){
        itemName = "emerald"
      }else  if(itemName === "Gold"){
        itemName = "gold"
      }else if(itemName === "Diamond"){
        itemName = "diamond"
      }else if(itemName === "Halloween Candy"){
        itemName = "halloweencandy"
      }else if(itemName === "Halloween%20Candy"){
        itemName = "halloweencandy"
      }else if(itemName === "Sheet%20Music"){
        itemName = "sheetmusic"
      }
      const itemImg = `/items/${itemName}.png`;
  
      // Force this to run after the current execution context
 
        addLog(
          <span>
            You found a <strong>{cleanName}</strong>!{" "}
            <img 
              src={itemImg} 
              alt={itemName} 
              style={{ width: 24, height: 24, verticalAlign: 'middle' }} 
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/items/pokeball.png'; // Fallback image
              }}
            />
          </span>
        );
   
    }

    setTimeout(() => {
      onBattleEnd?.({
        winner: 'player',
        opponent_type: initialData.opponent_type,
        floor: initialData.battle_tower_floor
      });
    }, 3000);
  } else {
    // Player lost
    addLog(`You were defeated by ${sanitizeLog('foe')}...`);

    setTimeout(() => {
      onBattleEnd?.({
        winner: 'foe',
        opponent_type: initialData.opponent_type,
        floor: initialData.battle_tower_floor
      });
    }, 3000);

  }
}


      // Item drop handling moved to battle_over block

 if (res.battle_over && didPlayerWin(res) && res.battle_coins_awarded > 0) {
    logBattleCoins(res.battle_coins_awarded);
  }

      return res;
    })
  , [battleId, yourMon, opponent, addLog, onBattleEnd, fetchAction, initialData, logBattleCoins, onCompleteFinal, triggerAnimation, battleOver]);

  // Potion use handler
const handlePotionUse = async (itemId, targetSlot = null) => {
  if (animationConfig || battleOver) return;
  
  // Create payload with target_slot if provided
  const payload = { 
    action: 'item', 
    item_id: itemId
  };
  
  // Add target_slot to payload if provided
  if (targetSlot !== null) {
    payload.target_slot = targetSlot;
  }
  
  // Use item action with target slot
  const res = await fetchAction(payload);
  if (!res) return;
  
  // For PvP, handle waiting state
  if (opponent_type === "pvp" && res.waiting_for_opponent) {
    if (!waitingForOpponent) {
      setWaitingForOpponent(true);
      setIsMyTurn(false); // It's now the opponent's turn
      addLog("Waiting for opponent to choose their move...");
    }
    setInventory(inv => inv.map(it => it.id === itemId ? { ...it, amount: Math.max(0, it.amount - 1) } : it));
    setShowBag(false);
    return;
  }
  
  // Update inventory state count locally
  setInventory(inv => inv.map(it => it.id === itemId ? { ...it, amount: Math.max(0, it.amount - 1) } : it));
  
  // Close bag after use
  setShowBag(false);
  
  // For PvP, logs are already added in doAction or pvpUpdate effect
  if (opponent_type === "pvp") {
    // Just add the logs directly
    if (res.log && res.log.length > 0) {
      res.log.forEach(line => addLog(line));
    }
  } else {
    // NPC battle - custom log formatting
    if (res.log && res.log.length > 0) {
      // Find healing message and foe attack message
      let healMessage = res.log.find(log => log.includes("restored"));
      let foeAttackMessages = res.log.filter(log => log.includes("Foe used"));
      let otherMessages = res.log.filter(log => !log.includes("restored") && !log.includes("Foe used"));
      
      // Get the target Pokémon name
      const targetPokemon = targetSlot ? roster.find(p => p.slot === targetSlot) : null;
      const targetName = targetPokemon ? (targetPokemon.nickname || targetPokemon.name) : "your Pokémon";
      
      // Add custom healing message based on item type
      if (healMessage) {
        if (itemId === 29) {
          addLog(`You used a Potion on ${targetName} and restored 20 HP!`);
        } else if (itemId === 30) {
          addLog(`You used a Super Potion on ${targetName} and restored 50 HP!`);
        } else if (itemId === 31) {
          addLog(`You used a Hyper Potion on ${targetName} and restored 200 HP!`);
        } else if (itemId === 32) {
          addLog(`You used a Max Potion on ${targetName} and fully restored HP!`);
        } else if (itemId === 84) {
          addLog(`You used a Revive on ${targetName} and revived it with half HP!`);
        } else if (itemId === 85) {
          addLog(`You used a Max Revive on ${targetName} and revived it with full HP!`);
        } else if (itemId === 170) {
          addLog(`You used a Revival Herb on ${targetName} and revived it with full HP!`);
        } else if (itemId === 171) {
          addLog(`You used an Energy Root on ${targetName} and restored 200 HP!`);
        } else {
          addLog(sanitizeLog(healMessage));
        }
      }
      
      // Add foe attack messages
      foeAttackMessages.forEach(attackMsg => {
        addLog(sanitizeLog(attackMsg));
      });
      
      // Add any other messages
      otherMessages.forEach(msg => {
        addLog(sanitizeLog(msg));
      });
    }
  }

  // Update player and opponent with current HP
  setYourMon(mon => ({ 
    ...mon, 
    currentHp: res.player.current_hp
  }));
  
  // Always refresh the entire roster after item use to ensure HP values are correct
  api.get('/v1/roster', { withCredentials: true }).then(r => {
    const freshRoster = r.data;
    setRoster(freshRoster.map(p => ({
      ...p,
      _updateKey: Date.now() // Force re-render
    })));
  }).catch(console.error);

  // If the battle isn't over, trigger the opponent's attack animation
  const foeHealEvt = res.events && Array.isArray(res.events)
    ? res.events.find(e => e && e.type === 'heal' && e.side === 'foe')
    : null;

  if (!res.battle_over && foeHealEvt && typeof foeHealEvt.new_hp === 'number') {
    setOpponent(opp => ({ ...opp, currentHp: foeHealEvt.new_hp }));
    setTimeout(() => {
      triggerHealAnimation('foe', onCompleteFinal);
    }, 500);
    return;
  }

  if (!res.battle_over && res.used_move) {
    setTimeout(() => {
      triggerAnimation(res.used_move, opponentRef, playerRef, onCompleteFinal);
    }, 800);
  } else {
    onCompleteFinal();
  }
};

const handleMoveClick = useCallback(async mv => {
  if (animationConfig || battleOver || mv.current_pp <= 0 || waitingForOpponent) {
    return;
  }

  // For PvP, call doAction first and check if we're waiting
  if (opponent_type === "pvp") {
    const res = await doAction({ action: 'move', move: mv.name });
    // If waiting for opponent, doAction already handled it (set waiting state, added log)
    // Animations will be triggered when both_moves_executed is received
    return;
  }

  const pSpeed = yourMon?.stats?.spe || 0;
  const fSpeed = opponent?.stats?.spe || 0;
  const playerFirst = pSpeed >= fSpeed;

  if (playerFirst) {
    if (opponent_type === "trainer" || opponent_type === "battle_tower") {
      const res = await fetchAction({ action: 'move', move: mv.name });
      if (!res) {
        onCompleteFinal();
        return;
      }

      const foeHealEvt = res.events && Array.isArray(res.events)
        ? res.events.find(e => e && e.type === 'heal' && e.side === 'foe')
        : null;

      const runAfterPlayerAnim = () => {
        // Update player and opponent HP regardless of battle outcome
        if (res) {
          setYourMon(mon => ({ 
            ...mon, 
            currentHp: res.player.current_hp,
            moves: res.player.moves && res.player.moves.length > 0 ? res.player.moves : (mon.moves || [])
          }));
          
          setRoster(rs => rs.map(p => {
            const upd = res.party.find(m => m.slot === p.slot);
            return upd ? { ...p, current_hp: upd.current_hp } : p;
          }));
          
          const foeData = getFoeData(res);
          setOpponent(opp => ({
            ...opp,
            currentHp: foeData.current_hp,
            maxHp: foeData.stats.hp
          }));
        }

        applyExpUpdateFromResponse(res, yourMon?.slot);

        if (res && res.battle_over && didPlayerWin(res)) {
          setBattleOver(true);
          setOpponent(opp => ({ ...opp, currentHp: 0 }));

          const foeData = getFoeData(res);
          addLog(`🏆 You defeated ${foeData?.name || sanitizeLog('foe')}!`);
          if (typeof res.exp_gained === 'number' && res.exp_gained > 0) {
            addLog(`You gained ${res.exp_gained.toLocaleString()} EXP from defeating ${foeData?.name || sanitizeLog('foe')}!`);
          }
          if (res.battle_coins_awarded && res.battle_coins_awarded > 0) {
            logBattleCoins(res.battle_coins_awarded);
          }

          setTimeout(() => {
            onBattleEnd?.({
              winner: 'player',
              opponent_type: initialData.opponent_type,
              floor: initialData.battle_tower_floor
            });
          }, 3000);

          onCompleteFinal();
          return;
        }

        if (res && res.battle_over) {
          if (res.winner === 'foe') {
            setBattleOver(true);
            const foeData = getFoeData(res);
            addLog(`You were defeated by ${foeData?.name || 'the opponent'}...`);

            if (initialData.opponent_type === "battle_tower" && initialData.battle_tower_floor) {
              const floor = initialData.battle_tower_floor;
              addLog(`💀 You were defeated on floor ${floor}!`);
              addLog(`You will be reset to a checkpoint floor.`);

              setTimeout(() => {
                window.location.href = `/battle-tower?battle_result=loss&floor=${floor}`;
              }, 3000);
            } else {
              setTimeout(() => {
                onBattleEnd?.({ winner: 'foe', battle_over: true });
              }, 2000);
            }
          }
          onCompleteFinal();
          return;
        }

        // Process logs from the backend response
        if (res && res.log && res.log.length > 0) {
          res.log.forEach(line => addLog(sanitizeLog(line)));
        }

        // Check if trainer sent out new Pokemon after player's attack
        const hasSentOutLogInPlayerFirst = res.log && res.log.some(entry => 
          entry.toLowerCase().includes('battle tower trainer sent out') ||
          entry.toLowerCase().includes('trainer sent out') ||
          entry.toLowerCase().includes('sent out'));

        if (hasSentOutLogInPlayerFirst) {
          if (res) {
            const sentOutFoe = getFoeData(res);
            setOpponent(() => ({
              species: sentOutFoe?.name,
              level: sentOutFoe?.level,
              currentHp: sentOutFoe?.current_hp,
              maxHp: sentOutFoe?.stats?.hp,
              stats: sentOutFoe?.stats,
              moves: sentOutFoe?.moves,
              switchId: Date.now(),
            }));
          }
          setAnimationConfig({
            key: 'FoeEntranceAnimation',
            props: { opponentRef, onComplete: onCompleteFinal }
          });
          setAnimKey(k => k + 1);
          onCompleteFinal();
          return;
        }

        if (res && !res.battle_over && res.used_move) {
          triggerAnimation(res.used_move, opponentRef, playerRef, onCompleteFinal);
          return;
        }

        onCompleteFinal();
      };

      const runPlayerAnim = () => {
        triggerAnimation(mv.name, playerRef, opponentRef, runAfterPlayerAnim);
      };

      if (foeHealEvt && typeof foeHealEvt.new_hp === 'number') {
        setOpponent(opp => ({ ...opp, currentHp: foeHealEvt.new_hp }));
        triggerHealAnimation('foe', runPlayerAnim);
      } else {
        runPlayerAnim();
      }
      return;
    }

    // player goes first
    triggerAnimation(mv.name, playerRef, opponentRef, async () => {
      const res = await doAction({ action: 'move', move: mv.name });
      
      // Update player and opponent HP regardless of battle outcome
      if (res) {
        // Update player with current HP and moves (including PP)
        setYourMon(mon => ({ 
          ...mon, 
          currentHp: res.player.current_hp,
          moves: res.player.moves && res.player.moves.length > 0 ? res.player.moves : (mon.moves || []) // Preserve moves during recharge
        }));
        
        // Update roster with current HP values
        setRoster(rs => rs.map(p => {
          const upd = res.party.find(m => m.slot === p.slot);
          return upd ? { ...p, current_hp: upd.current_hp } : p;
        }));
        
        // Update opponent with current HP
        const foeData = getFoeData(res);
        setOpponent(opp => ({
          ...opp,
          currentHp: foeData.current_hp,
          maxHp: foeData.stats.hp
        }));
      }
      
      if (res && res.battle_over) {
        if (res.winner === 'foe') {
          // Handle defeat
          setBattleOver(true);
          const foeData = getFoeData(res);
          addLog(`You were defeated by ${foeData?.name || 'the opponent'}...`);

          // Check if this is a Battle Tower battle
          if (initialData.opponent_type === "battle_tower" && initialData.battle_tower_floor) {
            const floor = initialData.battle_tower_floor;
            addLog(`💀 You were defeated on floor ${floor}!`);
            addLog(`You will be reset to a checkpoint floor.`);

            // Redirect to Battle Tower with loss parameter after a delay
            setTimeout(() => {
              window.location.href = `/battle-tower?battle_result=loss&floor=${floor}`;
            }, 3000);
          } else {
            // For non-Battle Tower battles, call onBattleEnd
            setTimeout(() => {
              onBattleEnd?.({ winner: 'foe', battle_over: true });
            }, 2000);
          }
        }
        onCompleteFinal();
      } else if (res && !res.battle_over) {
        // Process logs from the backend response
        if (res.log && res.log.length > 0) {
          res.log.forEach(line => addLog(sanitizeLog(line)));
        }
        
        // Check if trainer sent out new Pokemon after player's attack
        const hasSentOutLogInPlayerFirst = res.log && res.log.some(entry => 
          entry.toLowerCase().includes('battle tower trainer sent out') ||
          entry.toLowerCase().includes('trainer sent out') ||
          entry.toLowerCase().includes('sent out'));
        
        //console.log('=== PLAYER FIRST EXECUTION PATH ===');
        //console.log('hasSentOutLogInPlayerFirst:', hasSentOutLogInPlayerFirst);
        //console.log('res.foe.name:', res.foe.name);
        
        // Check if new Pokemon was sent out after player's attack
        if (hasSentOutLogInPlayerFirst) {
          if (res) {
            const sentOutFoe = getFoeData(res);
            setOpponent(() => ({
              species: sentOutFoe?.name,
              level: sentOutFoe?.level,
              currentHp: sentOutFoe?.current_hp,
              maxHp: sentOutFoe?.stats?.hp,
              stats: sentOutFoe?.stats,
              moves: sentOutFoe?.moves,
              switchId: Date.now(),
            }));
          }
          setAnimationConfig({
            key: 'FoeEntranceAnimation',
            props: { opponentRef, onComplete: onCompleteFinal }
          });
          setAnimKey(k => k + 1);
          // Complete the turn after setting up the delayed sequence
          onCompleteFinal();
        } else {
          const foeHealEvt = res.events && Array.isArray(res.events)
            ? res.events.find(e => e && e.type === 'heal' && e.side === 'foe')
            : null;
          if (foeHealEvt && typeof foeHealEvt.new_hp === 'number') {
            setOpponent(opp => ({ ...opp, currentHp: foeHealEvt.new_hp }));
            triggerHealAnimation('foe', onCompleteFinal);
          } else if (res.used_move) {
            triggerAnimation(res.used_move, opponentRef, playerRef, onCompleteFinal);
          } else {
            onCompleteFinal();
          }
        }
      } else {
        onCompleteFinal();
      }
    });
  }  else {
  // foe goes first
  const res = await fetchAction({ action: 'move', move: mv.name });
  if (!res) {
      onCompleteFinal();
      return;
    }

    applyExpUpdateFromResponse(res, yourMon?.slot);
    if (res.battle_over) {
      if (res.item_drop) {
        let itemName = res.item_drop;
        let cleanName = res.item_drop;
        if(itemName === "Yellow Shard"){
          itemName = "yellowshard"
        }
        else if(itemName === "Metal Coat"){
          itemName ="metalcoat"
        }else if(itemName === "Lucky Punch"){
          itemName = "lucky-punch"
        }else if(itemName === "Dragon Fang"){
          itemName = "dragongfang"
        }else if(itemName === "Dragon%20Fang"){
          itemName = "dragongfang"
        }else if(itemName === "Lucky%20Punch"){
          itemName = "lucky-punch"
        }else if(itemName === "Metal%20Coat"){
          itemName ="metalcoat"
        }else if(itemName === "Lucky%20Punch"){
          itemName = "lucky-punch"
        }
        else if(itemName === "Ruby"){
          itemName = "ruby"
        }else if(itemName === "Sapphire"){
          itemName = "sapphire"
        }else if(itemName === "Emerald"){
          itemName = "emerald"
        }else  if(itemName === "Gold"){
          itemName = "gold"
        }else if(itemName === "Diamond"){
          itemName = "diamond"
        }else if(itemName === "Halloween Candy"){
          itemName = "halloweencandy"
        }else if(itemName === "Halloween%20Candy"){
          itemName = "halloweencandy"
        }
        const itemImg = `/items/${itemName}.png`;
    
   
          addLog(
            <span>
              You found a <strong>{cleanName}</strong>!{" "}
              <img 
                src={itemImg} 
                alt={itemName} 
                style={{ width: 24, height: 24, verticalAlign: 'middle' }} 
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/items/pokeball.png'; // Fallback image
                }}
              />
            </span>
          );
     
      }
      setBattleOver(true);

      //console.log(res.item_drop)
      
      if (res.winner === 'player') {
        // Use the actual player move that was clicked, not res.used_move (which is foe's move)
        const playerMoveUsed = mv.name || 'Tackle';
        
        // Trigger player's final attack animation
        triggerAnimation(playerMoveUsed, playerRef, opponentRef, () => {
          // After attack animation, set opponent HP to 0
          const foeData = getFoeData(res);
          setOpponent(opp => ({
            ...opp,
            currentHp: 0,
            maxHp: foeData.stats.hp
          }));
          
          // Handle player victory
          addLog(`🏆 You defeated ${foeData.name || 'the opponent'}!`);
          const exp = res?.exp_gained;
          if (typeof exp === 'number' && exp > 0) {
            addLog(`You gained ${exp.toLocaleString()} EXP from defeating ${foeData.name}!`);
          }
          
          // Always add battle coins message if present
          if (res.battle_coins_awarded && res.battle_coins_awarded > 0) {
            logBattleCoins(res.battle_coins_awarded);
          }

          // Check if this is a Battle Tower battle and handle victory
          if (initialData.opponent_type === "battle_tower" && initialData.battle_tower_floor) {
            const floor = initialData.battle_tower_floor;
            const isBoss = initialData.boss === 'maxie';

            // Show victory message with Magma Coin
            addLog(`🔥 You earned a Magma Coin! 🔥`);
            addLog(`You will be teleported to the next floor in 3 seconds.`);

            // Redirect to Battle Tower with victory parameter after a delay
            setTimeout(() => {
              window.location.href = `/battle-tower?battle_result=victory&floor=${floor}${isBoss ? '&boss=maxie' : ''}`;
            }, 3000);
          } else {
            // For non-Battle Tower battles, call onBattleEnd
            setTimeout(() => {
              onBattleEnd?.({ winner: 'player', battle_over: true });
            }, 3000);
          }
          
          onCompleteFinal();
        });
        return;
      }

      if (res.winner === 'foe') {
        // Update player HP to 0
        setYourMon(mon => ({ ...mon, currentHp: 0 }));
        
        addLog(`You were defeated by ${res.foe?.name || 'the opponent'}...`);
        
        // Get the move used by the foe
        let foeMoveUsed = res.used_move || 'Tackle';
      
        
        // Try to extract move from battle log if not available directly
        if (!foeMoveUsed || foeMoveUsed === 'Tackle') {
          if (res.log && res.log.length > 0) {
            const moveMatch = res.log[0].match(/used ([^!]+)!/);
            if (moveMatch && moveMatch[1]) {
              foeMoveUsed = moveMatch[1].trim();
              //console.log('Extracted move from log:', foeMoveUsed);
            }
          }
        }
        
        // Trigger the foe's attack animation
        triggerAnimation(foeMoveUsed, opponentRef, playerRef, () => {
          // After animation completes
          onCompleteFinal();
          
          // Check if this is a Battle Tower battle
          if (initialData.opponent_type === "battle_tower" && initialData.battle_tower_floor) {
            const floor = initialData.battle_tower_floor;
            addLog(`💀 You were defeated on floor ${floor}!`);
            addLog(`You will be reset to a checkpoint floor.`);

            // Redirect to Battle Tower with loss parameter after a delay
            setTimeout(() => {
              window.location.href = `/battle-tower?battle_result=loss&floor=${floor}`;
            }, 3000);
          } else {
            // For non-Battle Tower battles, call onBattleEnd
            setTimeout(() => {
              onBattleEnd?.({ winner: 'foe', battle_over: true });
            }, 3000);
          }
        });
        return;
      }
    }
    
    const foeHealEvt = res.events && Array.isArray(res.events)
      ? res.events.find(e => e && e.type === 'heal' && e.side === 'foe')
      : null;
    const moveUsed = res.used_move;

    // Check if trainer sent out new Pokemon before foe attacks
    const hasSentOutLogInFoeFirst = res.log && res.log.some(entry => 
      entry.toLowerCase().includes('battle tower trainer sent out') ||
      entry.toLowerCase().includes('trainer sent out') ||
      entry.toLowerCase().includes('sent out'));
    
    //console.log('=== FOE FIRST EXECUTION PATH ===');
    //console.log('hasSentOutLogInFoeFirst:', hasSentOutLogInFoeFirst);
    //console.log('res.foe.name:', res.foe.name);
    
    // Update opponent data if new Pokemon was sent out
    if (hasSentOutLogInFoeFirst) {
      //console.log("🚀 NEW POKEMON DETECTED IN FOE FIRST - Waiting 2 seconds before entrance");
      
      // Wait 2 seconds to show the defeated Pokemon, then trigger new Pokemon entrance
      setTimeout(() => {
        //console.log("🚀 UPDATING OPPONENT DATA WITH NEW POKEMON (FOE FIRST)");
        const foeData = getFoeData(res);
        setOpponent(prevOpponent => ({
          species: foeData.name, // New Pokemon sent out, use the new name
          level: foeData.level,
          currentHp: foeData.current_hp,
          maxHp: foeData.stats.hp,
          stats: foeData.stats,
          moves: foeData.moves,
          switchId: Date.now()
        }));
        

    
      }, 2000); // Wait 2 seconds as requested
    }

    // 1) Foe acts first (attack or heal)
    const doFoeFirstAnim = (onDone) => {
      if (foeHealEvt && typeof foeHealEvt.new_hp === 'number') {
        setOpponent(opp => ({ ...opp, currentHp: foeHealEvt.new_hp }));
        triggerHealAnimation('foe', onDone);
        return;
      }
      if (moveUsed) {
        triggerAnimation(moveUsed, opponentRef, playerRef, onDone);
        return;
      }
      onDone();
    };

    doFoeFirstAnim(() => {
      // Don't add individual logs here - they'll be processed from the full log array
      // Update player with current HP and moves (including PP)
      setYourMon(mon => ({ 
        ...mon, 
        currentHp: res.player.current_hp,
        moves: res.player.moves && res.player.moves.length > 0 ? res.player.moves : (mon.moves || []) // Preserve moves during recharge
      }));
      setRoster(rs => rs.map(p => {
        const upd = res.party.find(m => m.slot === p.slot);
        return upd ? { ...p, current_hp: upd.current_hp } : p;
      }));

      // CRITICAL: Check for victory FIRST when battle is over
      if (res.battle_over && res.winner === 'player') {
        // Player won without attacking (e.g., enemy used self-destruct)
        setBattleOver(true);

        // Process all logs from the response
        if (res.log && res.log.length > 1) {
          // Skip the first entry which is the foe attack (already displayed)
          res.log.slice(1).forEach(line => addLog(sanitizeLog(line)));
        }

        // Check if this is a Battle Tower battle and handle victory
        if (opponent_type === "battle_tower" && initialData.battle_tower_floor) {
          const floor = initialData.battle_tower_floor;
          const isBoss = initialData.boss === 'maxie';

          // Show victory message with Magma Coin
          addLog(`🔥 You earned a Magma Coin! 🔥`);
          addLog(`You will be teleported to the next floor in 3 seconds.`);

          // Redirect to Battle Tower with victory parameter after a delay
          setTimeout(() => {
            window.location.href = `/battle-tower?battle_result=victory&floor=${floor}${isBoss ? '&boss=maxie' : ''}`;
          }, 3000);
        }

        // First set opponent HP to 0 to show defeat visually
        setOpponent(opp => ({
          ...opp,
          currentHp: 0
        }));
        
        // Then trigger the player's attack animation to show the final blow
        triggerAnimation(mv.name, playerRef, opponentRef, () => {
          // After animation completes, call onCompleteFinal to clean up the animation
          onCompleteFinal();
          
          // For Battle Tower, we already have a redirect so don't need to call onBattleEnd
          if (opponent_type !== "battle_tower") {
            // Only call onBattleEnd for non-Battle Tower battles
            setTimeout(() => {
              onBattleEnd?.({ winner: 'player', battle_over: true });
            }, 6000); // Give time to see the victory state
          }
        });
        
        return;
      }

      // Check for loss ONLY if battle is over and player didn't win
      if (res.battle_over && res.winner !== 'player') {
        const allDead = roster.every(p => p.current_hp <= 0);
        if (res.player.current_hp <= 0 && allDead) {
          //console.log('DEFEAT DETECTED: All Pokémon fainted, battle_over=true, winner=foe');
          
          // First set player HP to 0 to show defeat visually
          setYourMon(mon => ({
            ...mon,
            currentHp: 0
          }));
          
          // Then trigger the foe's attack animation to show the final blow
          // Use a generic attack name if we don't know what move was used
          // First check if we have a move in the response
          let foeMoveUsed = 'Tackle'; // Default fallback
          
          if (res.used_move) {
            // If the server provided the move directly, use it
            foeMoveUsed = res.used_move;
            //console.log('Using move from response:', foeMoveUsed);
          } else if (res.log && res.log.length > 0) {
            // Try to extract from log
            const moveMatch = res.log[0].match(/used ([^!]+)!/);
            if (moveMatch && moveMatch[1]) {
              foeMoveUsed = moveMatch[1].trim();
              //console.log('Extracted move from log:', foeMoveUsed);
            }
          }
          
          //console.log('Triggering foe attack animation with move:', foeMoveUsed);
          
          // Force a small delay to ensure state updates have completed
          setTimeout(() => {
            triggerAnimation(foeMoveUsed, opponentRef, playerRef, () => {
              // After animation completes, call onCompleteFinal to clean up
              onCompleteFinal();
              
              // Add defeat log messages
              addLog("All your Pokémon have fainted.");
              
              // Check if this is a Battle Tower battle
              if (opponent_type === "battle_tower" && initialData.battle_tower_floor) {
                const floor = initialData.battle_tower_floor;
                addLog(`💀 You were defeated on floor ${floor}!`);
                addLog("You will be returned to the last checkpoint floor.");
                
                // Redirect to Battle Tower with loss parameter after a delay
                setTimeout(() => {
                  window.location.href = `/battle-tower?battle_result=loss&floor=${floor}`;
                }, 3000);
              } else {
                // For non-Battle Tower battles, call onBattleEnd
                setTimeout(() => {
                  //console.log('DEFEAT DETECTED: Calling onBattleEnd with foe winner');
                  onBattleEnd?.({
                    winner: 'foe',
                    opponent_type: initialData.opponent_type,
                    floor: initialData.battle_tower_floor
                  });
                }, 2000);
              }
            });
          }, 500); // Short delay to ensure state updates have completed
          
          return;
        }
      }

      // If player's active Pokémon fainted but they have more, end this turn
      if (res.player.current_hp <= 0 && !res.battle_over) {
        onCompleteFinal();
        return;
      }

      // 2) Then player attacks (if battle isn't over)
      if (!res.battle_over) {
        triggerAnimation(mv.name, playerRef, opponentRef, () => {
        // Don't add individual logs here - they'll be processed from the full log array

        // Update opponent with current HP
        const foeData = getFoeData(res);
        setOpponent(opp => ({
          ...opp,
          currentHp: foeData.current_hp,
          maxHp: foeData.stats.hp
        }));

        // Check for battle end after player's attack
        //console.log('BattleScreen: Checking battle end - battle_over:', res.battle_over, 'winner:', res.winner);
     
        // Only trust backend battle_over flag for defeat detection
        if (res.battle_over && res.winner === 'foe') {
          //console.log('DEFEAT CONDITION DETECTED: Backend confirmed battle over with foe winner');
          setBattleOver(true);
          addLog(`You were defeated by ${foeData?.name || 'the opponent'}...`);

          // Check if this is a Battle Tower battle
          if (initialData.opponent_type === "battle_tower" && initialData.battle_tower_floor) {
            const floor = initialData.battle_tower_floor;
            addLog(`💀 You were defeated on floor ${floor}!`);
            addLog(`You will be reset to a checkpoint floor.`);

            // Redirect to Battle Tower with loss parameter after a delay
            setTimeout(() => {
              window.location.href = `/battle-tower?battle_result=loss&floor=${floor}`;
            }, 3000);
          } else {
            // For non-Battle Tower battles, call onBattleEnd
            setTimeout(() => {
              onBattleEnd?.({ 
                winner: 'foe', 
                battle_over: true,
                opponent_type: initialData.opponent_type,
                floor: initialData.battle_tower_floor
              });
            }, 2000);
          }
          onCompleteFinal();
          return;
        }

        // If player's active Pokemon fainted but battle isn't over, they need to switch
        if (res.player.current_hp <= 0 && !res.battle_over) {
          console.log("Player's active Pokemon fainted but battle continues - they need to switch");
          onCompleteFinal();
          return;
        }

        const hasSentOutLog = res.log && res.log.some(entry => 
          entry.toLowerCase().includes('battle tower trainer sent out') ||
          entry.toLowerCase().includes('trainer sent out') ||
          entry.toLowerCase().includes('sent out'));
        
        //console.log('=== MANUAL TRIGGER CHECK ===');
        //console.log('res.log:', res.log);
        //console.log('hasSentOutLog:', hasSentOutLog);
        //console.log('Current forceEntranceAnimation:', forceEntranceAnimation);
        
        // Manually trigger entrance animation when trainer sends out new Pokemon
        if(hasSentOutLog){

        } else {
          //console.log("No sent out log found");
        }

        if (res.battle_over && res.winner === 'player') {
          //console.log('=== VICTORY DETECTED ===');
          //console.log('opponent_type:', opponent_type);
          //console.log('initialData.opponent_type:', initialData.opponent_type);
          //console.log('initialData.battle_tower_floor:', initialData.battle_tower_floor);
          //console.log('initialData:', initialData);
          
          // Use the original logs for better accuracy and to capture all messages like gummy activations
          if (res.log && res.log.length > 2) {
            // Filter out duplicate EXP messages to prevent double logging
            const uniqueLogs = res.log.slice(2).filter((line, index, arr) => {
              // Remove duplicate EXP messages
              if (line.includes('You gained') && line.includes('EXP')) {
                return arr.findIndex(l => l.includes('You gained') && l.includes('EXP')) === index;
              }
              // Remove duplicate defeat messages
              if (line.includes('Defeated trainer Pokemon')) {
                return arr.findIndex(l => l.includes('Defeated trainer Pokemon')) === index;
              }
              return true;
            });
            uniqueLogs.forEach(line => addLog(sanitizeLog(line)));
          } else {
            // Fallback to custom messages if logs aren't properly structured
            const foeData = getFoeData(res);
            addLog(`🏆 You defeated ${foeData.name || 'the opponent'}!`);
            const exp = res?.exp_gained;
            if (typeof exp === 'number' && exp > 0) {
              addLog(`You gained ${exp.toLocaleString()} EXP from defeating ${foeData.name}!`);
            }
          }
          // Always add battle coins message if present
          if (res.battle_coins_awarded && res.battle_coins_awarded > 0) {
            logBattleCoins(res.battle_coins_awarded);
          }

          // Check if this is a Battle Tower battle and handle victory
          if (opponent_type === "battle_tower" && initialData.battle_tower_floor) {
            const floor = initialData.battle_tower_floor;
            const isBoss = initialData.boss === 'maxie';

            // Show victory message with Magma Coin
            addLog(`🔥 You earned a Magma Coin! 🔥`);
            addLog(`You will be teleported to the next floor in 3 seconds.`);

            // Redirect to Battle Tower with victory parameter after a delay
            setTimeout(() => {
              window.location.href = `/battle-tower?battle_result=victory&floor=${floor}${isBoss ? '&boss=maxie' : ''}`;
            }, 3000);
          }

          // Only end battle if it's actually over
          if (res.battle_over) {
            onBattleEnd?.({ winner: 'player', battle_over: true });
          }
        } else if (res.battle_over && res.winner === 'foe') {

          
          setBattleOver(true);
          addLog(`You were defeated by ${res.foe?.name || 'the opponent'}...`);

          // Check if this is a Battle Tower battle and handle defeat
          if (opponent_type === "battle_tower" && initialData.battle_tower_floor) {
            const floor = initialData.battle_tower_floor;

            // Show defeat message
            addLog(`💀 You were defeated on floor ${floor}!`);
            addLog(`You will be reset to a checkpoint floor.`);

            // Redirect to Battle Tower with loss parameter after a delay
            setTimeout(() => {
              window.location.href = `/battle-tower?battle_result=loss&floor=${floor}`;
            }, 3000);
          } else {
            // For non-Battle Tower battles, call onBattleEnd
            setTimeout(() => {
              onBattleEnd?.({
                winner: 'foe', 
                battle_over: true,
                opponent_type: initialData.opponent_type,
                floor: initialData.battle_tower_floor
              });
            }, 2000);
          }
        }
        // Process all logs from the backend response at the end
        if (res.log && res.log.length > 0) {
          res.log.forEach(line => addLog(sanitizeLog(line)));
        }
        
        onCompleteFinal();
      });
    } else {
      // Battle is already over, just complete
      onCompleteFinal();
    }

  });
}

}, [
  animationConfig,
  battleOver,
  yourMon?.stats?.spe,
  opponent?.stats?.spe,
  roster,
  triggerAnimation,
  doAction,
  fetchAction,
  onCompleteFinal,
  addLog,
  onBattleEnd,
  logBattleCoins,
  sanitizeLog,
  initialData.battle_tower_floor,
  initialData.boss,
  initialData.opponent_type,
  opponent_type
]);

// Pokemon switch handler
const handleSwitch = useCallback(async (slot) => {
  
  if (animationConfig || battleOver) {
    return;
  }
  
  // Prevent switching to the same Pokemon that's currently active
  if (slot === yourMon?.slot) {
    return;
  }
  
  // Clear recharge state when switching Pokemon
  setMustRecharge(false);
  
  // Send switch request to the server
  const res = await doAction({ action: 'switch', slot });
  if (!res) {
    return;
  }
  

  
  // Update your active Pokémon with the new one
  const updatedParty = res.party.find(p => p.slot === slot);
  // Reset exp tracking to prevent false level-up animations when switching
  expInitializedRef.current = false;
  displayedLevelRef.current = null;
  
  setYourMon({
    species: updatedParty.species, // Use the Pokemon from the target slot
    level: updatedParty.level,
    totalexp: updatedParty.totalexp, // Store totalexp for level calculation
    currentHp: updatedParty.current_hp,
    maxHp: updatedParty.max_hp,
    stats: res.player.stats, // Keep stats from response (may be updated)
    moves: res.player.moves && res.player.moves.length > 0 ? res.player.moves : [], // Preserve moves during recharge
    slot: slot,
    switchId: Date.now() // Force re-render of Pokemon image
  });
  
  setRoster(rs => rs.map(p => {
    const upd = res.party.find(m => m.slot === p.slot);
    return upd ? { ...p, current_hp: upd.current_hp } : p;
  }));

  // log the switch - use the Pokemon from the slot we're switching to
  const switchedToPokemon = res.party.find(p => p.slot === slot);
  addLog(`You switched to ${switchedToPokemon?.species || res.player.name}!`);

  // now decide: foe attacks only if switching from healthy Pokemon (not fainted)
  const moveUsed = res.used_move || 'Tackle';
  // Check if there's actually a foe attack in the logs
  // Look for any log entry that contains "foe used" or "trainer sent out" followed by "foe used"
  const hasFoeAttackLog = res.log && res.log.some(entry => entry.toLowerCase().includes('foe used'));
 


  // Determine if foe should attack based on exp_gained field and attack log presence
  const shouldFoeAttack = res.hasOwnProperty('exp_gained') && hasFoeAttackLog;
  const doPostAttackUpdate = () => {
        // apply the damage from foe if any in the response log
        if (res.player.current_hp != null) {
          setYourMon(mon => ({ ...mon, currentHp: res.player.current_hp }));
          setRoster(rs => rs.map(p => {
            const upd = res.party.find(m => m.slot === p.slot);
            return upd ? { ...p, current_hp: upd.current_hp } : p;
          }));
        }
        // log foe's attack text
        if (res.log?.length > 1) addLog(sanitizeLog(res.log[1])); // Use second log entry for foe attack
        onCompleteFinal();
      };

      if (shouldFoeAttack) {
        // If we have both "sent out" and "foe used" in the logs, we need to handle both animations
      triggerAnimation(moveUsed, opponentRef, playerRef, doPostAttackUpdate);
        
      } else {
        // no animation, just update
        onCompleteFinal();
      }
  }, [
    animationConfig,
    battleOver,
    doAction,
    triggerAnimation,
    opponentRef,
    playerRef,
    setYourMon,
    setRoster,
    onCompleteFinal,
    addLog,
    sanitizeLog
]);

// Initialize the UI





  if (!yourMon || !opponent) return null;

  // Ensure HP percentages never exceed 100% to prevent bar overflow
  const playerHpPct   = Math.min(100, (yourMon?.currentHp   / yourMon?.maxHp  ) * 100) || 0;
  const opponentHpPct = Math.min(100, (opponent?.currentHp / opponent?.maxHp) * 100) || 0;

  const showExpHud = opponent_type !== "pvp";
  const expTotal = showExpHud
    ? (Number(displayedExp) || 0)
    : (Number(yourMon?.totalexp || 0) || 0);
  const expLevel = getLevel(expTotal);
  const expBase = Math.pow(expLevel, 3);
  const expNext = Math.pow(expLevel + 1, 3);
  const playerExpPct = expNext > expBase
    ? Math.max(0, Math.min(100, ((expTotal - expBase) / (expNext - expBase)) * 100))
    : 0;

  const getHpColor = pct => {
    if (pct <= 25) return '#dc3545';      // red
    if (pct <= 50) return '#ffc107';      // yellow
    return '#28a745';                     // green
  };

  return (
    <div className="battle-overlay">
      <div className="battle-container">
        <div className="battle-bg">
          <img src={battleBgUrl} className="battle-bg-img" alt="Battle background" />
          <div className="hp-bar opponent-hp">
            <span>{opponent.species} Lv.{opponent.level || 1}</span>
            <div
              className="hp-fill"
              style={{ width: `${opponentHpPct}%`, '--hp-color': getHpColor(opponentHpPct) }}
            />
          </div>
          {showExpHud ? (
            <div className="hp-exp-container player-hud">
              <div className="hp-bar">
                <span>{yourMon.nickname||yourMon.species} Lv.{expLevel || (yourMon.level || 1)}</span>
                <div
                  className="hp-fill"
                  style={{ width: `${playerHpPct}%`, '--hp-color': getHpColor(playerHpPct) }}
                />
              </div>
              <div className="exp-bar" aria-label="Experience">
                <div className="exp-fill" style={{ width: `${playerExpPct}%` }} />
              </div>
              {levelUpEvent && (
                <div key={levelUpEvent.key} className="level-up-badge">
                  LEVEL UP!
                </div>
              )}
            </div>
          ) : (
            <div className="hp-bar player-hp">
              <span>{yourMon.nickname||yourMon.species} Lv.{yourMon.totalexp ? getLevel(yourMon.totalexp) : (yourMon.level || 1)}</span>
              <div
                className="hp-fill"
                style={{ width: `${playerHpPct}%`, '--hp-color': getHpColor(playerHpPct) }}
              />
            </div>
          )}
          <div className="sprite-wrapper wild" key={`opponent-wrapper-${opponent.switchId || opponent.species}`}>
               <img
   key={`${opponent.species}-${opponent.switchId || opponent.currentHp}`}
    ref={opponentRef}
    src={`/sprites/${opponent.species}.png`}
    alt={opponent.species}
    className="wild-front"
    style={{ position: 'absolute', top: '22%', right: '10%', zIndex: 10 }}
    onError={e => {
      e.currentTarget.onerror = null;
      e.currentTarget.src = `/icon/${opponent.species}.png`;
    }}
  />
          </div>
          <div className="sprite-wrapper player">
            {(() => {
              const active = roster.find(p=>p.slot===yourMon?.slot);
              const key    = active ? active.name : yourMon?.species;
              const alt    = active ? (active.nickname||active.name) : (yourMon.nickname||yourMon?.species);
              
              // Debug logging for sprite issues
              if (!key || key === 'undefined') {
                console.error("Player sprite key is undefined:", {
                  active,
                  yourMon,
                  roster,
                  slot: yourMon?.slot
                });
              }
              
              return <img ref={playerRef}
                          key={`player-${yourMon?.species}-${yourMon?.switchId || yourMon?.slot}`}
                          src={`/BackSprites/${key || 'placeholder'}.png`}
                          alt={alt}
                          className="player-back"/>;
            })()}
          </div>
          {animationConfig && React.createElement(
            BattleAnimations[animationConfig.key],
            { key: animKey, ...animationConfig.props }
          )}
        </div>
        {/* ─── GUMMY STATUS OVERLAY ─── */}
<div className="gummy-status-overlay">
  {Object.entries(gummyStatus).map(([key, val]) => {
    if (val <= 0) return null;
    let icon;
    switch (key) {
      case "ruby_gummy":     icon = rubyGummyImg;     break;
      case "sapphire_gummy": icon = sapphireGummyImg; break;
      case "emerald_gummy":  icon = emeraldGummyImg;  break;
      case "golden_gummy":   icon = goldenGummyImg;   break;
      case "diamond_gummy":  icon = diamondGummyImg;  break;
      default: return null;
    }
    // “Ruby Gummy” from “ruby_gummy”
    const label = key
      .replace("_gummy","")
      .split("_")
      .map(w => w[0].toUpperCase() + w.slice(1))
      .join(" ") + " Gummy";
    return (
      <div key={key} style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}>
        <img src={icon} alt={label} style={{ width: 16, height: 16 }} />
        <span>{val}× {label}</span>
      </div>
    );
  })}
</div>
        <div className="battle-ui-bottom">
          {/* PvP Turn Indicator */}
          {opponent_type === "pvp" && (
            <div style={{
              textAlign: "center",
              padding: "8px",
              backgroundColor: isMyTurn ? "rgba(34, 197, 94, 0.2)" : "rgba(239, 68, 68, 0.2)",
              borderRadius: "8px",
              marginBottom: "10px",
              border: `2px solid ${isMyTurn ? "#22c55e" : "#ef4444"}`
            }}>
              <strong style={{ color: isMyTurn ? "#22c55e" : "#ef4444" }}>
                {isMyTurn ? "YOUR TURN" : "OPPONENT'S TURN"}
              </strong>
              {/* Debug info */}
              <div style={{ fontSize: "10px", marginTop: "4px", opacity: 0.7 }}>
                Turn: {isMyTurn ? "✓" : "✗"} | Waiting: {waitingForOpponent ? "✓" : "✗"} | Battle: {battleOver ? "Over" : "Active"}
              </div>
            </div>
          )}
          
          <div className="actions">
            <div className="moves">
              {yourMon.moves.map((mv,i)=>
                <button key={i}
                        disabled={
                          battleOver ||
                          yourMon.currentHp<=0 ||
                          (mv.current_pp <= 0) ||
                          (opponent_type === "pvp" && !isMyTurn) ||
                          waitingForOpponent ||
                          mustRecharge
                        }
                        onClick={()=>handleMoveClick(mv)}
                        className={`move-btn move-${mv.type.toLowerCase()}`}
                        title={
                          mustRecharge ? "Your Pokemon must recharge!" :
                          yourMon.currentHp<=0 ? "Your Pokemon has fainted! Switch to another Pokemon." :
                          (opponent_type === "pvp" && !isMyTurn) ? "Wait for your turn!" :
                          (mv.current_pp <= 0) ? "No PP left!" : ""
                        }>
                  <div>{mv.name}</div>
                  <div className="move-pp-counter">
                    PP: {mv.current_pp}/{mv.max_pp}
                  </div>
                </button>
              )}
            </div>
            <div className="switch-grid">
              {roster.map(p=>{
                const fainted = p.current_hp<=0;
                const active  = p.slot===yourMon?.slot;
                return (
                  <button key={`${p.slot}-${p.id || p.owned_id || p.name || p.nickname || ''}`}
                          className={
                            "switch-slot"+
                            (active?" active":"")+
                            (fainted?" fainted":"")}
                          disabled={battleOver||animationConfig||fainted||active}
                          onClick={()=>{ if(!fainted&&!active) handleSwitch(p.slot); }}>
                    <img src={`/icon/${p.name}.png`} className="switch-icon" alt={p.name}/>
                    <div className="switch-hp-bar">
                      <div className="switch-hp-fill"
                           style={{
                             width: `${(p.current_hp/p.max_hp)*100}%`,
                             backgroundColor: getHpColor((p.current_hp/p.max_hp)*100)
                           }}/>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="battle-quick-actions">
            <button
              className={`bag-btn ${yourMon && yourMon.currentHp <= 0 ? "disabled" : ""}`}
              onClick={toggleBag}
              disabled={yourMon && yourMon.currentHp <= 0}
              title={yourMon && yourMon.currentHp <= 0 ? "Cannot open bag while fainted" : "Open bag"}
            >
              <img
                src={pouchIcon}
                alt="Bag"
                className="bag-btn-icon"
              />
              Bag
            </button>
            {opponent_type === "wild" && (
              <button
                className="run-btn"
                onClick={() => {
                  if (initialData?.engagement === "aggro") {
                    addLog("You can't run from an aggressive Pokémon!");
                    return;
                  }
                  setRanAway(true);
                  setBattleOver(true);
                  addLog("You ran away safely!");
                  setTimeout(() => onExit(), 1500);
                }}
                disabled={!!animationConfig || initialData?.engagement === "aggro"}
                style={initialData?.engagement === "aggro" ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                {initialData?.engagement === "aggro" ? "Can't Run!" : "Run Away"}
              </button>
            )}
            {opponent_type === "trainer" && (
              <button
                className="run-btn"
                onClick={onExit}
                disabled={!!animationConfig}
              >
                End Battle
              </button>
            )}
          </div>
        </div>
        <div className="battle-log" ref={logRef}>
  {logs.map((line, i) => <div key={i}>{line}</div>)}
</div>
      </div>

      {/* Battle bag overlay */}
      {showBag && (
        <div className="bag-overlay" style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999,
          color: '#fff',
          padding: '10px'
        }}>
          <BattleInventoryPanel
            inventory={inventory}
            selectedAction={selectedAction}
            setSelectedAction={setSelectedAction}
            onClose={toggleBag}
            roster={roster}
            onItemUse={handlePotionUse}
            battleType={initialData.opponent_type === "battle_tower" ? 
              (initialData.tower_type || "endless") : "wild"}
          />
          {/* Click outside closes bag */}
          <div style={{position:'fixed',top:0,left:0,width:'100%',height:'100%'}} onClick={toggleBag}/>
        </div>
      )}
    </div>
  );
}
